/* Shared pieces of the visit review workspace: the status vocabulary, the record modal every
   section opens into, and the reject-with-a-reason flow.

   Visual reference: docs/design/visit-detail.html. Normative rules: docs/design/spec/. */

import React, { useState } from "react";
import { Button, Descriptions, Empty, Flex, Input, Modal, Tag, Typography } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";

import { VISIT_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import { translate } from "../../util/util";

/** The three kinds a supervisor can moderate. Everything else carries a document status it does
    not own — orders, returns and payments come from Excalibur and never enter the queue. */
export const MODERATABLE = ["report", "answer", "pricemonitoringreport"];

/** Which section a moderated record belongs to, so only that one repaints. */
export const TYPE_SECTION = { report: "reports", answer: "tasks", pricemonitoringreport: "price" };

/** A visit with no `end` is still open — the merchandiser is in the shop. It takes no verdict
    until it closes: the API refuses one (a PATCH to an open visit would also stamp its `end`). */
export const isActive = visit => !visit?.end;

/** An `Approval` row is written only once someone acts: no row, or `is_approved: null`, is
    still awaiting a decision. Same tri-state as the visit's own `is_approved`. */
export const statusOf = approval => (approval?.is_approved === true ? "approved" : approval?.is_approved === false ? "rejected" : "pending");

const TONE = {
  approved: { tone: "ok", Icon: CheckCircleOutlined, key: "approved_single_visit_name" },
  rejected: { tone: "no", Icon: CloseCircleOutlined, key: "no_approved_single_visit_name" },
  pending: { tone: "wait", Icon: ClockCircleOutlined, key: "on_verification_single_visit_name" },
  /* A task nobody has answered — a resting state, not a fault, so the neutral tone. */
  awaiting: { tone: "off", Icon: MinusCircleOutlined, key: "awaiting_answer_name" },
  /* An open visit — nothing to review yet. */
  active: { tone: "off", Icon: PlayCircleOutlined, key: "visit_in_progress" },
};

/**
 * The status pill: a `-bg` tint, a `-700` label and an icon — never colour alone.
 *
 * Shape and tone come from `.status-tag` in `theme/antd.scss`, which the photo report's pill also
 * uses; antd's `<Tag color="success">` is banned for both because it derives its label from the
 * `-500` FILL tokens and fails AA (spec/01#status-colour).
 */
export function StatusTag({ status, lang, className = "" }) {
  const { tone, Icon, key } = TONE[status] || TONE.pending;
  return (
    <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone} ${className}`}>
      {translate(VISIT_PAGE, key, lang)}
    </Tag>
  );
}

/** `POST approvals/` — the one endpoint behind every approve/reject on this page.
    ⚠ `isOk`, not `Boolean`: `call()` hands a TRUTHY `{results:[],count:0,failed:true}` to the
    callback on 5xx/network failure, so truthiness scored failures as successes (#1581 #10). */
export function postApproval({ objectId, objectType, isApproved, comment = "" }, done) {
  call({ method: "POST", url: "approvals/", post: { object_id: objectId, object_type: objectType, is_approved: isApproved, comment } }, response =>
    done(isOk(response))
  );
}

export const dash = <span className="vd-dash">—</span>;

/** A value cell that says "—" rather than rendering an empty row. */
const shown = value => (value === null || value === undefined || value === "" ? dash : value);

/**
 * The record modal every section opens into.
 *
 * `sections` are either key/value `rows` — rendered as an antd `<Descriptions>`, per spec/02 —
 * or a `node` escape hatch for a section that is not key/value (a photo, a nested table).
 *
 * Header and footer are the Modal's own; nothing inside is sticky, because a sticky header would
 * occlude a line-items table's own sticky `thead` (see the modal notes in CLAUDE.md).
 */
export function RecordModal({ open, record, lang, onClose }) {
  if (!record) return null;
  const t = key => translate(VISIT_PAGE, key, lang);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={record.wide ? 1000 : 720}
      className="vd-modal"
      destroyOnHidden
      title={
        <Flex align="flex-start" gap="small" wrap>
          <Flex vertical flex="1" className="vd-modal__id">
            <Typography.Text strong className="vd-modal__t">
              {record.title}
            </Typography.Text>
            {record.subtitle ? (
              <Typography.Text type="secondary" className="vd-modal__s">
                {record.subtitle}
              </Typography.Text>
            ) : null}
          </Flex>
          {record.status ? <StatusTag status={record.status} lang={lang} /> : null}
        </Flex>
      }
      footer={[
        ...(record.actions || []).map(action => (
          <Button
            key={action.key}
            type={action.tone === "no" ? "primary" : action.tone === "ok" ? "default" : "default"}
            danger={action.tone === "no"}
            className={action.tone === "ok" ? "vd-btn--ok" : ""}
            icon={action.icon}
            onClick={action.onClick}>
            {action.label}
          </Button>
        )),
        <Button key="close" onClick={onClose}>
          {t("surveys_modal_close")}
        </Button>,
      ]}>
      <Flex vertical gap="middle">
        {(record.sections || []).filter(Boolean).map((section, index) => (
          <section key={section.titleKey || index}>
            {section.titleKey ? (
              <div className="vd-modal__h">
                {section.icon}
                {t(section.titleKey)}
                {section.count != null ? <span className="vd-cnt">{section.count}</span> : null}
              </div>
            ) : null}
            {section.node != null ? (
              section.node
            ) : (
              <Descriptions className="vd-desc" column={1} size="small" bordered={false}>
                {(section.rows || []).filter(Boolean).map(([label, value, opts]) => (
                  <Descriptions.Item key={label} label={label} className={opts?.num ? "vd-desc__num" : ""}>
                    {shown(value)}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </section>
        ))}
      </Flex>
    </Modal>
  );
}

/**
 * Reject asks for a reason.
 *
 * The legacy tabs posted `comment: ""` every time, so a rejected record could never say why —
 * which is the one thing the merchandiser needs in order to fix it.
 */
export function RejectModal({ open, target, lang, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const t = key => translate(VISIT_PAGE, key, lang);

  return (
    <Modal
      open={open}
      /* The title names the action and the subtitle names the record, so the two do not echo. */
      title={t("reject_btn")}
      className="vd-modal"
      okText={t("reject_btn")}
      cancelText={translate(VISIT_PAGE, "surveys_modal_close", lang)}
      okButtonProps={{ danger: true }}
      afterOpenChange={visible => visible || setReason("")}
      onCancel={onCancel}
      onOk={() => onConfirm(reason.trim())}>
      <Flex vertical gap="small">
        {target?.title ? <Typography.Text type="secondary">{target.title}</Typography.Text> : null}
        <Input.TextArea autoFocus rows={3} value={reason} placeholder={t("reject_reason_ph")} onChange={event => setReason(event.target.value)} />
      </Flex>
    </Modal>
  );
}

/** Every section's zero-row state. */
export function SectionEmpty({ text }) {
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text} />;
}
